import { useEffect, useState } from "react";
import { api, ASSET_TYPE_LABELS, OWNER_LABELS, assetColor } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { WarningCircle, TrendUp, Buildings, Coins, Scales } from "@phosphor-icons/react";

export default function NetWorth() {
  const { fmtMoney, fmtAsset } = useDemo();
  const { hid, active, ownerLabel } = useHousehold();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!hid) return;
    api.get(`/networth?household_id=${hid}`).then((r) => setData(r.data)).catch(() => {});
  }, [hid]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading net worth…</div>;

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{active?.name}</p>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Consolidated Net Worth</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-primary text-primary-foreground rounded-sm p-6 shadow-[0_8px_30px_hsl(218_44%_28%_/_0.18)]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground/60">Net Worth</p>
          <p data-testid="networth-total" className="font-display text-4xl font-black tracking-tighter mt-2 tabular-nums">{fmtAsset(data.net_worth ?? data.total)}</p>
          <p className="text-xs text-primary-foreground/60 mt-2">{data.asset_count} assets · {data.entity_count} entities</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <TrendUp size={22} weight="bold" className="mb-2 text-[hsl(var(--asset-real_estate))]" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Total Assets</p>
          <p data-testid="networth-assets" className="font-mono text-2xl font-black mt-2 tabular-nums">{fmtAsset(data.assets_total ?? data.total)}</p>
        </div>
        <div className={`rounded-sm p-6 border ${(data.liabilities_total || 0) > 0 ? "border-destructive/30 bg-destructive/5" : "border-border bg-card"}`}>
          <Scales size={22} weight="bold" className="mb-2 text-destructive" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Liabilities</p>
          <p data-testid="networth-liabilities" className="font-mono text-2xl font-black mt-2 tabular-nums text-destructive">{fmtAsset(data.liabilities_total || 0)}</p>
        </div>
        <div className={`rounded-sm p-6 border ${data.needs_review_count > 0 ? "border-warning/40 bg-warning/10" : "border-border bg-card"}`}>
          <WarningCircle size={22} weight={data.needs_review_count > 0 ? "fill" : "bold"} className={`mb-2 ${data.needs_review_count > 0 ? "text-warning" : ""}`} />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Needs Review</p>
          <p data-testid="networth-needs-review" className="font-display text-3xl font-black mt-2 tabular-nums">{data.needs_review_count}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="By Asset Class" icon={Coins} rows={data.by_type.map((t) => ({ label: ASSET_TYPE_LABELS[t.key] || t.key, amount: t.amount, color: assetColor(t.key) }))} total={data.total} testid="by-type" />
        <Panel title="By Entity" icon={Buildings} rows={data.by_entity.map((e, i) => ({ label: e.name, amount: e.amount, color: `hsl(var(--chart-${(i % 5) + 1}))` }))} total={data.total} testid="by-entity" />
        <Panel title="By Owner" icon={TrendUp} rows={data.by_owner.map((o, i) => ({ label: ownerLabel(o.owner_party), amount: o.amount, color: `hsl(var(--chart-${(i % 5) + 1}))` }))} total={data.total} testid="by-owner" />
      </div>
    </div>
  );
}

const Panel = ({ title, icon: Icon, rows, total, testid }) => {
  const { fmtAsset } = useDemo();
  return (
    <div data-testid={`networth-${testid}`} className="bg-card border border-border rounded-sm p-5 shadow-[0_4px_20px_hsl(var(--ploy-ink)/0.04)]">
      <div className="flex items-center gap-2 mb-4"><Icon size={16} weight="bold" className="text-primary" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{title}</p></div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
        <div className="space-y-3">
          {rows.map((r, i) => {
            const pct = total > 0 ? Math.round((r.amount / total) * 100) : 0;
            return (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium truncate pr-2 flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: r.color }} />{r.label}</span>
                  <span className="font-mono tabular-nums font-semibold">{fmtAsset(r.amount)}</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-sm overflow-hidden"><div className="h-full rounded-sm" style={{ width: `${pct}%`, background: r.color }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
